import React from 'react';
import { useTranslation } from 'react-i18next';
import CreateApplication from 'Modules/fortiva/routes/CreateApplication';
import FinancingPreparation from 'Modules/fortiva/components/FinancingPreparation';
import { useGetNextStep } from 'Modules/fortiva/hooks/useGetNextStep';
import { useGetPaymentInfo } from 'Core/hooks/platform/useGetPaymentInfo';
import Loader from 'Core/components/Loader';
import FinancingSuccess from 'Modules/fortiva/routes/FinancingSuccess';
import FinancingApproved from 'Modules/fortiva/routes/FinancingApproved';
import ReturnTracking from 'Modules/fortiva/components/ReturnTracking';
import FinancingStatusMessage from 'Modules/fortiva/components/FinancingStatusMessage';
import FinancingDeclined from 'Modules/fortiva/routes/FinancingDeclined';
import FinancingAlreadyProcessed from 'Modules/fortiva/routes/FinancingAlreadyProcessed';
import FinancingExceptions from 'Modules/fortiva/routes/FinancingExceptions';

import { cardPreferenceCenterPath, paymentPath } from 'Modules/cardPayment/routes';
import constants from 'Modules/fortiva/config/constants';
import {
  financingPath,
  financingSuccessPath,
  financingErrorPath,
  mixedDownPaymentCheckoutPath,
} from 'Modules/fortiva/routes';
import { errorPath } from 'Core/routes';
import {
  hasOtpMismatch,
  hasAlreadyHasAccount,
  hasExistingCustomer,
} from 'Modules/fortiva/utils/correctionStatus';
import useErrorStore, { setErrorActionSelector } from 'Core/stores/error';
import { useNavigate } from 'react-location';
import { createFinancingPath as allegroFinancingPath } from 'Modules/financing/routes';

export default function FinancingRedirect() {
  const routes = {
    paymentOptions: cardPreferenceCenterPath,
    financing: financingPath,
    mixed: mixedDownPaymentCheckoutPath,
    financingSuccessPath,
    financingErrorPath,
    payment: paymentPath,
    allegroFinancing: allegroFinancingPath,
  };
  const navigate = useNavigate();
  const setError = useErrorStore(setErrorActionSelector);
  const { t } = useTranslation();

  const [customerIdentityNumber, setCustomerIdentityNumber] = React.useState('');
  const rawSessionStorageItem = sessionStorage.getItem(constants.SESS_STORAGE_APPLICATION_KEY);
  const cachedApp =
    rawSessionStorageItem && rawSessionStorageItem !== 'undefined'
      ? JSON.parse(rawSessionStorageItem)
      : null;
  const { data: newApp, isLoading, isError, error } = useGetNextStep(false, !cachedApp);
  const application = cachedApp || newApp;
  sessionStorage.removeItem(constants.SESS_STORAGE_APPLICATION_KEY);
  const { next_action = 'CREATE_APPLICATION' } = application || {};
  const {
    data: { amount_to_pay, customer_id = '' } = {},
    isLoading: isPaymentInfoLoading,
    isError: isPaymentInfoError,
  } = useGetPaymentInfo();

  if (isLoading) {
    return <Loader message={t('status.loading.financingApplication')} />;
  }
  // else if (provider && provider !== 'FORTIVA') {
  //   navigate({ to: routes.allegroFinancing, search: true });
  //   return <CreateApplication customer_id={customer_id} />;
  // }

  // No application found? -> Show form to create one
  else if (isError || error?.response?.status === 404 || !application?.next_action) {
    return (
      <CreateApplication
        customer_id={customer_id}
        onCustomerIdentityNumber={setCustomerIdentityNumber}
      />
    );
  } else if (application?.next_action) {
    switch (next_action) {
      case 'CREATE_APPLICATION':
        return (
          <CreateApplication
            type="primary"
            customer_id={customer_id}
            onCustomerIdentityNumber={setCustomerIdentityNumber}
          />
        );
      case 'SELECT_OFFER':
      case 'SIGN_CONTRACT':
        return (
          <FinancingPreparation
            application={application}
            routes={routes}
            customerIdentityNumber={customerIdentityNumber}
          />
        );
      case 'UPDATE_OFFER':
        // Existing customer match on offer update -> show the already-processed page
        if (hasExistingCustomer(application)) {
          return <FinancingAlreadyProcessed />;
        }
        return (
          <FinancingPreparation
            application={application}
            routes={routes}
            customerIdentityNumber={customerIdentityNumber}
          />
        );
      case 'ERROR':
        // Invalid OTP -> let the user re-enter the code on the finalize tab
        if (hasOtpMismatch(application)) {
          return (
            <FinancingPreparation
              application={application}
              routes={routes}
              customerIdentityNumber={customerIdentityNumber}
            />
          );
        }
        // Customer already has an account -> application was already processed
        if (hasAlreadyHasAccount(application)) {
          return <FinancingAlreadyProcessed />;
        }
        setError(t('status.error.getFinancingApplicationFailed'));
        navigate({ to: errorPath, search: true });
        break;
      case 'CORRECTION_REQUIRED':
      case 'DECLINED':
      case 'DECISION_PENDING':
      case 'INCOMPLETE':
        return (
          <FinancingExceptions
            customer_id={customer_id}
            application={application}
            nextAction={next_action}
            onCustomerIdentityNumber={setCustomerIdentityNumber}
          />
        );
      case 'UPDATE_AMOUNT':
        return <FinancingApproved />;
      case 'CANCELLED':
        return <FinancingDeclined application={application} />;
      case 'DONE':
      case 'FUND_APPLICATION':
      case 'PENDING_DOWN_PAYMENT':
        return <FinancingSuccess />;
      case 'RETURN_APPROVED':
      case 'RETURN_REQUESTED':
      case 'RETURN_PROCESSING':
      case 'RETURN_COMPLETE':
      case 'RETURN_CANCELED':
        return <ReturnTracking nextAction={next_action} />;
      case 'APPLICATION_CLOSED':
        if (application?.payload?.application_status === 'Rejected') {
          return <FinancingDeclined application={application} isRejected />;
        }
        return <FinancingStatusMessage nextAction={next_action} />;
      case 'NO_DECISION':
        return <FinancingStatusMessage nextAction={next_action} />;
      default:
        setError(t('status.error.getFinancingApplicationFailed'));
        navigate({ to: errorPath, search: true });
    }
  }

  if (!isPaymentInfoError && !isPaymentInfoLoading && !amount_to_pay) {
    return <FinancingApproved />;
  }

  return <Loader message={t('status.loading.financingApplication')} />;
}
